/**
 * OrgMemberPicker — self-contained org-member picker popover with search.
 *
 * Wraps a caller-supplied trigger (e.g. a "Reassign" Button, or an avatar) in
 * a `DialogTrigger` + `Popover` with a filter-as-you-type search field over
 * the org's members (react-aria `Autocomplete` + `useFilter`; matching is on
 * name AND email via each row's textValue). Selecting a member calls
 * `onSelect` with their entity_id and closes the popover. Pass `onUnassign`
 * to additionally offer an "Unassign" row (the assignee-row use; the footer's
 * "Reassign" omits it).
 *
 * Modeled on `MemoOwnerPicker`/`UserOption` in
 * `lib/crm/record/memo/MemoOwnerPicker.tsx`, but built on react-aria
 * `ListBox`/`ListBoxItem` instead of a hand-rolled
 * `<ul><li><button role='option'>` — this gives arrow-key roving-tabindex
 * navigation, typeahead, and selection semantics for free. Kept as a
 * deliberate small duplication (both the footer's "Reassign" and the
 * inline-editing Assignee row can share one implementation; a later refactor
 * can migrate MemoOwnerPicker to this picker) — but the member-display
 * helpers (`deriveInitials`, `memberDisplayName`, `getMemberAvatarSrc`) are
 * shared from `../memo/memoMembers` so they can't drift between the two.
 */

import { SearchLg } from '@untitledui/icons';
import type { Membership } from 'api-client';
import type { ReactNode } from 'react';
import { useCallback, useMemo, useState } from 'react';
import {
  Autocomplete,
  Dialog,
  DialogTrigger,
  Input as AriaInput,
  ListBox,
  ListBoxItem,
  SearchField,
  useFilter,
} from 'react-aria-components';

import { useOrgMembers } from '@/hooks/useOrgMembers';
import { Avatar } from '@/lib/base/avatar/avatar';
import { Popover } from '@/lib/base/overlays/popover';
import { useCrmObjectSnapshots } from '@/lib/crm/hooks';
import { cx } from '@/utils/cx';

import {
  deriveInitials,
  filterAssignableMembers,
  getMemberAvatarSrc,
  memberDisplayName,
} from '../memo/memoMembers';

// ── Module-level constants (stable references, never recreated) ────────────

// Panel chrome (and enter/exit animation) lives on the lib/base Popover, which
// also owns outside-click dismissal. `py-0` drops the wrapper's list padding so
// the search header sits flush; `w-auto min-w-56` replace its trigger-width
// sizing. The Dialog keeps the height cap so the member list — not the whole
// panel — scrolls beneath the pinned search field.
const POPOVER_CLASS = 'w-auto min-w-56 max-w-72 py-0 shadow-xl';

const DIALOG_CLASS = 'flex flex-col max-h-72 outline-hidden';

const LIST_ITEM_CLASS =
  'flex items-center gap-2 rounded-md px-2 py-1.5 text-sm outline-none cursor-pointer transition-colors';

// Search header: a borderless input row with a bottom divider, Linear-style —
// the popover itself provides the chrome, so the field must not render its
// own ring/box (a bordered input inside a small popover reads as clutter).
const SEARCH_FIELD_CLASS =
  'flex items-center gap-2 px-3 py-2 border-b border-secondary';
const SEARCH_INPUT_CLASS =
  'w-full min-w-0 appearance-none bg-transparent text-sm text-primary placeholder:text-placeholder outline-hidden';

const UNASSIGN_KEY = '__unassign__';

// Stable renderEmptyState for the filtered list (module-level, never
// recreated). Shown when the search matches no members.
const renderNoMatches = () => (
  <p className='px-2 py-1.5 text-sm text-tertiary'>No members match</p>
);

// Stable className render-prop for the Unassign row (module-level).
const unassignRowClassName = ({ isFocused }: { isFocused: boolean }) =>
  cx(
    LIST_ITEM_CLASS,
    'text-secondary hover:bg-primary_hover',
    isFocused && 'bg-primary_hover'
  );

/** Row model for the picker's single dynamic collection. */
type PickerRow =
  | { id: string; kind: 'member'; member: Membership }
  | { id: string; kind: 'unassign' };

interface MemberRowProps {
  member: Membership;
  isSelected: boolean;
  src: string | undefined;
}

function MemberRow({ member, isSelected, src }: MemberRowProps) {
  return (
    <ListBoxItem
      id={member.entityId}
      // Name + email so the Autocomplete filter matches either — the row
      // displays only the name, but typing an email fragment still finds it.
      textValue={`${memberDisplayName(member)} ${member.email}`}
      // The list deliberately has NO react-aria selectionMode (see the ListBox
      // below), so the current assignee is conveyed by the sr-only suffix in
      // the row content (ListBoxItem does not forward custom aria-* props);
      // the data attribute is the styling/testing hook.
      data-current-assignee={isSelected ? 'true' : undefined}
      className={({ isFocused }) =>
        cx(
          LIST_ITEM_CLASS,
          isSelected
            ? 'bg-primary_hover text-primary'
            : 'text-secondary hover:bg-primary_hover',
          isFocused && !isSelected && 'bg-primary_hover'
        )
      }
    >
      <Avatar
        size='xs'
        src={src}
        initials={deriveInitials(member)}
        alt={memberDisplayName(member)}
      />
      <span className='flex-1 truncate'>{memberDisplayName(member)}</span>
      {isSelected && <span className='sr-only'>(current assignee)</span>}
    </ListBoxItem>
  );
}

export interface OrgMemberPickerProps {
  orgId: number | undefined;
  selectedEntityId: string | null | undefined;
  onSelect: (entityId: string) => void;
  /**
   * When provided, an "Unassign" row is offered below the members and picking
   * it fires this callback (instead of `onSelect`).
   */
  onUnassign?: () => void;
  /** Pressable trigger, e.g. a lib/base Button labeled "Reassign". */
  children: ReactNode;
  /** Gate the member/avatar fetches; pass false to keep them idle. */
  enabled?: boolean;
  /**
   * Prevent the popover from opening. A disabled lib/base `Button` trigger is
   * not enough on its own: when the trigger carries a `disabledTooltip` it
   * renders through the gated `aria-disabled` path (button.tsx), which keeps
   * react-aria's `usePress` active so `DialogTrigger` can still open the
   * popover on press. Pass this while the trigger is disabled so the open is
   * gated at the source. `enabled` only idles fetches — it does NOT stop the
   * popover from opening.
   */
  isDisabled?: boolean;
  /** Accessible name for the picker dialog. Defaults to 'Reassign'. */
  'aria-label'?: string;
}

/**
 * Self-contained org-member picker popover. The passed-in trigger is
 * expected to be a labeled lib/base `Button` (or another react-aria
 * pressable) — it forwards to react-aria's `Button` under the hood
 * (`button.tsx:292`), which `DialogTrigger` requires to wire the press
 * source. The MemoOwnerPicker precedent avoids lib/base `Button` only for
 * icon-only avatar alignment, which does not apply to a text trigger like
 * "Reassign".
 */
export function OrgMemberPicker({
  orgId,
  selectedEntityId,
  onSelect,
  onUnassign,
  children,
  enabled = true,
  isDisabled = false,
  'aria-label': ariaLabel = 'Reassign',
}: OrgMemberPickerProps) {
  // The popover is uncontrolled by the caller, so own its open state here to
  // gate the per-member CRM snapshot fetch. Every mounted footer would
  // otherwise fetch a snapshot for every active org member up-front, on every
  // task view; deferring the subscription until the popover actually opens
  // keeps that object-fetch work off the common (never-reassign) path.
  const [isOpen, setIsOpen] = useState(false);

  // Gate opening while disabled: DialogTrigger requests open via onOpenChange
  // on trigger press, and a `disabledTooltip` trigger keeps usePress active
  // (see the isDisabled prop doc), so swallowing the open request here is what
  // actually keeps the popover shut. Closing (open === false) is always
  // honored so an in-flight open can't be stranded if disabled flips true.
  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open && isDisabled) {
        return;
      }
      setIsOpen(open);
    },
    [isDisabled]
  );

  const { members } = useOrgMembers(enabled === false ? undefined : orgId);
  // Only active, non-partner members are valid reassignment targets — mirrors
  // the backend's owner-member rules. See filterAssignableMembers.
  const assignableMembers = useMemo(
    () => filterAssignableMembers(members),
    [members]
  );
  const memberEntityIds = useMemo(
    () => assignableMembers.map(m => m.entityId),
    [assignableMembers]
  );
  // Subscribe to member avatar snapshots only while the popover is open (and
  // the picker is enabled). Closed → pass [] / undefined so nothing is fetched.
  const snapshotsEnabled = enabled !== false && isOpen;
  const memberObjects = useCrmObjectSnapshots(
    snapshotsEnabled ? memberEntityIds : [],
    snapshotsEnabled ? orgId : undefined
  );

  const handleAction = useCallback(
    (key: React.Key) => {
      if (key === UNASSIGN_KEY) {
        onUnassign?.();
        return;
      }
      onSelect(String(key));
    },
    [onSelect, onUnassign]
  );

  // Case/diacritic-insensitive substring match for the Autocomplete filter.
  const { contains } = useFilter({ sensitivity: 'base' });

  // ONE dynamic collection via the `items` prop. Mixing a `.map()` array with
  // a conditional JSX sibling inside the ListBox splits the rows across two
  // react-aria collection instances under Autocomplete (observed: the extra
  // row carried a different data-collection id), leaving it dead to clicks
  // and desyncing the input's aria-activedescendant so Enter activated
  // nothing.
  const rows: PickerRow[] = useMemo(() => {
    const memberRows: PickerRow[] = assignableMembers.map(member => ({
      id: member.entityId,
      kind: 'member' as const,
      member,
    }));
    // Offer "Unassign" only when the caller handles it AND someone is
    // actually assigned.
    if (onUnassign && selectedEntityId) {
      memberRows.push({ id: UNASSIGN_KEY, kind: 'unassign' as const });
    }
    return memberRows;
  }, [assignableMembers, onUnassign, selectedEntityId]);

  const renderRow = useCallback(
    (row: PickerRow) =>
      row.kind === 'member' ? (
        <MemberRow
          member={row.member}
          isSelected={row.member.entityId === selectedEntityId}
          src={getMemberAvatarSrc(memberObjects.get(row.member.entityId))}
        />
      ) : (
        <ListBoxItem
          id={UNASSIGN_KEY}
          textValue='Unassign'
          className={unassignRowClassName}
        >
          Unassign
        </ListBoxItem>
      ),
    [selectedEntityId, memberObjects]
  );

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={handleOpenChange}>
      {children}
      {/* lib/base Popover: non-modal, outside-click dismissal (incl. trigger
          re-click-to-close, which lands in handleOpenChange(false)), the portal
          event boundary that keeps search-field keys out of the task card, and
          the stacking z-index are all owned by the wrapper. */}
      <Popover
        size='md'
        placement='bottom end'
        offset={4}
        shouldFlip
        containerPadding={16}
        className={POPOVER_CLASS}
      >
        <Dialog aria-label={ariaLabel} className={DIALOG_CLASS}>
          {({ close }) => (
            <Autocomplete filter={contains}>
              <SearchField
                aria-label='Search members'
                autoFocus
                className={SEARCH_FIELD_CLASS}
              >
                <SearchLg
                  aria-hidden='true'
                  className='size-4 shrink-0 text-fg-quaternary'
                />
                <AriaInput
                  placeholder='Search members'
                  className={SEARCH_INPUT_CLASS}
                />
              </SearchField>
              {/* Deliberately NO selectionMode: this is an ACTION list.
                With `selectionMode` + `onAction` + a non-empty controlled
                selection, react-aria demotes onAction (useSelectableItem's
                hasPrimaryAction requires an EMPTY selection) — a mouse
                click then performs an inert "selection" (no
                onSelectionChange handler) and the row appears dead, and
                Autocomplete's Enter (a synthetic click with no
                pointerType) activates nothing. Without selection, every
                click/Enter is the action. The current assignee is marked
                via MemberRow's aria-current + styling instead. */}
              <ListBox
                aria-label='Members'
                data-component='org-member-picker'
                className='flex-1 overflow-y-auto p-1 outline-hidden'
                renderEmptyState={renderNoMatches}
                items={rows}
                onAction={key => {
                  handleAction(key);
                  close();
                }}
              >
                {renderRow}
              </ListBox>
            </Autocomplete>
          )}
        </Dialog>
      </Popover>
    </DialogTrigger>
  );
}
